import { useEffect, useState } from "react";
import { Alert, Container, Spinner } from "react-bootstrap";
import { FaBell, FaChevronRight, FaShieldAlt } from "react-icons/fa";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { calculateNgcmfaExpiration, getAccessToken } from "../utils/tokenUtils";
import racqFavicon from "../images/RACQ Favicon.svg";
import pageBackgroundImage from "../images/Page-background-image.png";
import { SecurityAlert, UserProfileHeader, ToastNotifications } from "./common";
import PasskeysSection from "./passkeys/PasskeysSection";
import leftArrowIcon from "../images/LeftArrow.svg";
import { useIdentity } from "../hooks/identity";
import {
  links,
  menu,
  pageFooterLinks,
  sidebarFooterLinks,
} from "../constants/links";
import racqLogo from "../images/logo.png";

const NGCMFA_EXPIRY_MINUTES = 15;
const SECONDS_PER_MINUTE = 60;

export const SecurityPage = () => {
  const { instance, accounts } = useMsal();
  const { getIdentity, fetchIdentity } = useIdentity();
  const [accessToken, setAccessToken] = useState(null);
  const [bearerToken, setBearerToken] = useState(null);
  const [ngcmfaExpiration, setNgcmfaExpiration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessTokenError, setAccessTokenError] = useState(null);
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    void fetchAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  useEffect(() => {
    if (accessToken) {
      const expiration = calculateNgcmfaExpiration(
        accessToken,
        NGCMFA_EXPIRY_MINUTES,
        SECONDS_PER_MINUTE,
      );
      setNgcmfaExpiration(expiration);
    } else {
      setNgcmfaExpiration(null);
    }
  }, [accessToken]);

  const getUserId = () => {
    if (accessToken?.oid) {
      return accessToken.oid;
    }
    return null;
  };

  const fetchAccessToken = async () => {
    const redirectToLogin = async () => {
      try {
        await instance.loginRedirect(loginRequest);
      } catch (loginError) {
        console.error("Login redirect failed:", loginError);
      }
    };

    try {
      const result = await getAccessToken(instance, accounts, loginRequest);

      if (result.error) {
        setAccessTokenError(result.error);
        setLoading(false);
        await redirectToLogin();
      } else {
        setAccessTokenError(null);
        setAccessToken(result.decodedToken);
        setBearerToken(result.token);
        setLoading(false);
        fetchIdentity(result.idToken);
      }
    } catch (error) {
      setAccessTokenError(`Failed to get access token: ${error.message}`);
      setLoading(false);
      await redirectToLogin();
    }
  };

  const getUserData = () => {
    const defaultUserData = {
      name: "User",
      email: "user@example.com",
    };

    if (accessToken) {
      return {
        name:
          accessToken.name ||
          accessToken.given_name ||
          accessToken.family_name ||
          defaultUserData.name,
        email:
          accessToken.unique_name ||
          accessToken.email ||
          accessToken.preferred_username ||
          accessToken.upn ||
          defaultUserData.email,
      };
    }

    return defaultUserData;
  };

  const displayError = accessTokenError;
  const userData =
    !loading && !accessTokenError
      ? getUserData()
      : { name: "Loading...", email: "Loading..." };
  const userId = !loading && !accessTokenError ? getUserId() : null;

  const alerts = [
    {
      id: 1,
      message:
        "For your security, multi-factor authentication is required when managing your credentials",
      type: "info",
      icon: FaBell,
    },
  ];

  const showToast = (toastData) => {
    // Check if this is a sessionExpiredWithAction toast and if one already exists
    if (toastData.type === "sessionExpiredWithAction") {
      const existingSessionExpiredToast = toasts.find(
        (toast) => toast.type === "sessionExpiredWithAction" && toast.show,
      );

      // If a session expired toast is already showing, don't add another one
      if (existingSessionExpiredToast) {
        return;
      }
    }

    const newToast = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      show: true,
      ...toastData,
    };
    setToasts((prev) => [...prev, newToast]);
  };

  const closeToast = (toastId) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== toastId));
  };

  const handleLogout = async () => {
    try {
      const allAccounts = instance.getAllAccounts();

      if (allAccounts.length === 0) {
        await instance.clearCache();
        await instance.logoutRedirect();
        return;
      }

      await instance.logoutRedirect({
        account: allAccounts[0],
      });
    } catch (error) {
      console.error("Logout redirect failed:", error);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (displayError) {
    return (
      <Container className="py-4">
        <Alert variant={accessTokenError ? "danger" : "warning"}>
          <Alert.Heading>
            {accessTokenError ? "Authentication Error" : "Service Error"}
          </Alert.Heading>
          <p>{displayError}</p>
        </Alert>
      </Container>
    );
  }

  if (!userId) {
    return (
      <Container className="py-4">
        <Alert variant="warning">
          <Alert.Heading>User ID Not Available</Alert.Heading>
          <p>
            Unable to extract user ID from token claims. Please try logging in
            again.
          </p>
        </Alert>
      </Container>
    );
  }

  const memberSinceYear = getIdentity()?.joined?.slice(0, 4);

  return (
    <div className="security-page">
      <div className="security-page-background" aria-hidden="true">
        <img src={pageBackgroundImage} alt="Background" />
      </div>

      <aside className="security-sidebar">
        <div className="security-sidebar-header">
          <img src={racqFavicon} alt="RACQ Icon" />
        </div>

        <div className="security-user-card">
          <div className="security-avatar">
            {userData.name
              .split(" ")
              .map((part) => part.charAt(0).toUpperCase())
              .slice(0, 2)
              .join("")}
          </div>
          <div className="security-user-name">{userData.name}</div>
          <div className="security-user-meta">
            Blue member since {memberSinceYear}
          </div>
          <button type="button" className="security-membership-btn">
            Membership card
          </button>
        </div>

        {/* Main Menu START */}
        <nav className="security-sidebar-nav" aria-label="Primary">
          {menu.map((item) => (
            <a
              key={item.label}
              href={item.link}
              className={`security-nav-item ${item.label === "Profile" ? "security-nav-item-active" : ""}`.trim()}
            >
              <img src={item.icon} alt={item.alt} />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        {/* Main Menu END */}

        {/* Sub Menu START */}
        <div className="security-sidebar-footer-links">
          {sidebarFooterLinks.map((item) => (
            <a
              href={item.link}
              key={item.label}
              type="button"
              target="_blank"
              className="security-link-btn text-decoration-none"
              rel="noreferrer"
            >
              {item.label}
            </a>
          ))}
        </div>
        {/* Sub Menu END */}

        <button
          type="button"
          className="security-logout-btn"
          onClick={handleLogout}
        >
          Log out
        </button>
      </aside>

      <main className="security-main">
        <div className="security-main-content">
          <div className="security-breadcrumbs" aria-label="Breadcrumb">
            <a href={links.accountOverview} className="text-decoration-none">
              Home
            </a>
            <FaChevronRight className="security-breadcrumb-sep" />
            <a href={links.profile} className="text-decoration-none">
              Profile
            </a>
            <FaChevronRight className="security-breadcrumb-sep" />
            <span className="security-breadcrumb-current">Manage passkeys</span>
          </div>

          <UserProfileHeader
            name={userData.name}
            email={userData.email}
            icon={FaShieldAlt}
          />

          <hr className="security-divider" />

          {alerts.map((alert) => (
            <SecurityAlert
              key={alert.id}
              message={alert.message}
              type={alert.type}
              icon={alert.icon}
            />
          ))}

          <PasskeysSection
            onShowToast={showToast}
            accessToken={bearerToken}
            userId={userId}
            ngcmfaExpiry={ngcmfaExpiration}
          />

          <a className="security-back-btn" href={links.profile}>
            <img
              src={leftArrowIcon}
              alt="back icon"
              className="me-1"
              style={{
                height: 12,
                width: 12,
              }}
            />
            <span>Back</span>
          </a>

          <hr className="security-divider" />

          <section className="security-things-to-note">
            <h5>Things to note</h5>
            <p>
              RACQ Group collect, hold, use and disclose your personal
              information in line with the{" "}
              <a href={links.privacy} onClick={(e) => e.preventDefault()}>
                RACQ Group Privacy Policy
              </a>{" "}
              and relevant privacy collection notice.
            </p>
            <p className="mb-0">
              If you hold an RACQ insurance product, RACQ Group and RACQ
              Insurance collect, hold, use and disclose your personal
              information in line with the{" "}
              <a href={links.privacy} onClick={(e) => e.preventDefault()}>
                RACQ Group Privacy Policy
              </a>{" "}
              and the{" "}
              <a
                href={links.insurancePrivacy}
                onClick={(e) => e.preventDefault()}
              >
                RACQ Insurance Privacy Collection Notice
              </a>
              .
            </p>
          </section>
        </div>

        <footer className="security-page-footer d-flex">
          <div>
            <div className="security-footer-links mt-1">
              {pageFooterLinks.map((item) => (
                <a
                  key={item.label}
                  href={item.link}
                  target="_blank"
                  rel="noreferrer"
                  className="security-link-btn text-decoration-none"
                >
                  {item.label}
                </a>
              ))}
            </div>
            <div className="security-footer-copy d-flex mt-3">
              <div className="py-1">
                © RACQ Operations Pty Ltd {new Date().getFullYear()}
              </div>
              <div className="ms-4 border rounded px-3 py-1">BSB: 514-179</div>
            </div>
          </div>

          <div className="ms-auto me-4">
            <img src={racqLogo} alt="racq logo" style={{ width: 105 }} />
          </div>
        </footer>
      </main>

      <ToastNotifications toasts={toasts} onCloseToast={closeToast} />
    </div>
  );
};

export default SecurityPage;
